export type ReadinessStatus = 'ready' | 'blocked' | 'advisory';

export interface ReadinessItem {
  id: string;
  status: ReadinessStatus;
  title: string;
  detail?: string;
}

export interface ReadinessInput {
  hasContent: boolean;
  isDirty: boolean;
  hasConflict: boolean;
  unknownVariableCount: number | null;
  changeNote: string;
}

const pluralise = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

export function publishReadiness({
  hasContent,
  isDirty,
  hasConflict,
  unknownVariableCount,
  changeNote,
}: ReadinessInput): ReadinessItem[] {
  const items: ReadinessItem[] = [];

  if (hasConflict) {
    items.push({
      id: 'conflict',
      status: 'blocked',
      title: 'Draft changed elsewhere',
      detail: 'Load the latest draft before publishing.',
    });
  }

  if (!hasContent) {
    items.push({ id: 'content', status: 'blocked', title: 'No content yet', detail: 'Write the body before publishing.' });
  }

  items.push(
    isDirty
      ? { id: 'saved', status: 'blocked', title: 'Changes not saved', detail: 'Save your changes before publishing.' }
      : { id: 'saved', status: 'ready', title: 'All changes saved' },
  );

  if (unknownVariableCount !== null) {
    items.push(
      unknownVariableCount === 0
        ? { id: 'variables', status: 'ready', title: 'All variables recognised' }
        : {
            id: 'variables',
            status: 'advisory',
            title: pluralise(unknownVariableCount, 'unknown variable'),
            detail: 'Not in the variable catalogue. The server checks this when you save.',
          },
    );
  }

  items.push(
    changeNote.trim()
      ? { id: 'change-note', status: 'ready', title: 'Change note added' }
      : { id: 'change-note', status: 'advisory', title: 'No change note', detail: 'Optional, but shown in version history.' },
  );

  return items;
}

export const publishBlockedReason = (items: ReadinessItem[]): string | null =>
  items.find((item) => item.status === 'blocked')?.detail ?? null;
